import { useAuthStore } from '@/stores/authStore'
import type { ApiResponse, AuthResponse, LoginRequest, SignupRequest, SignupResponse } from '@/types'

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000'

/**
 * Base fetch wrapper with auth and error handling
 */
async function fetchWithAuth<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<ApiResponse<T>> {
  const { token } = useAuthStore.getState()
  
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string> | undefined),
  }

  if (token) {
    headers['Authorization'] = `Bearer ${token}`
  }
  
  try {
    const response = await fetch(`${API_URL}${endpoint}`, {
      ...options,
      headers,
    })
    
    if (response.status === 401) {
      const data = await response.json().catch(() => ({}))
      const isAuthEndpoint = endpoint.startsWith('/api/auth/login') || endpoint.startsWith('/api/auth/signup')
      if (!isAuthEndpoint && token) {
        useAuthStore.getState().logout()
        return { success: false, error: 'Session expired. Please login again.' }
      }
      return {
        success: false,
        error: data.detail || data.message || 'Invalid username or password',
      }
    }
    
    const data = await response.json()
    
    if (!response.ok) {
      return { 
        success: false, 
        error: data.detail || data.message || 'An error occurred' 
      }
    }
    
    return { success: true, data }
  } catch (error) {
    console.error('API Error:', error)
    return { 
      success: false, 
      error: error instanceof Error ? error.message : 'Network error' 
    }
  }
}

/**
 * API client methods
 */
export const api = {
  // Auth
  auth: {
    login: async (credentials: LoginRequest): Promise<ApiResponse<AuthResponse>> => {
      return fetchWithAuth<AuthResponse>('/api/auth/login', {
        method: 'POST',
        body: JSON.stringify(credentials),
      })
    },

    signup: async (data: SignupRequest): Promise<ApiResponse<SignupResponse>> => {
      return fetchWithAuth<SignupResponse>('/api/auth/signup', {
        method: 'POST',
        body: JSON.stringify(data),
      })
    },
    
    logout: async (): Promise<ApiResponse<void>> => {
      return fetchWithAuth<void>('/api/auth/logout', {
        method: 'POST',
      })
    },
    
    me: async (): Promise<ApiResponse<{ user: AuthResponse['user'] }>> => {
      return fetchWithAuth('/api/auth/me')
    },
  },
  
  // Projects
  projects: {
    list: async () => {
      return fetchWithAuth('/api/projects')
    },
    
    get: async (id: string) => {
      return fetchWithAuth(`/api/projects/${id}`)
    },
    
    create: async (data: { name: string; description?: string; tags?: string[] }) => {
      return fetchWithAuth('/api/projects', {
        method: 'POST',
        body: JSON.stringify(data),
      })
    },
    
    update: async (id: string, data: Partial<{ name: string; description: string; status: string }>) => {
      return fetchWithAuth(`/api/projects/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      })
    },
    
    delete: async (id: string) => {
      return fetchWithAuth(`/api/projects/${id}`, {
        method: 'DELETE',
      })
    },
    
    getProgress: async (id: string) => {
      return fetchWithAuth(`/api/projects/${id}/progress`)
    },
    
    getTree: async (id: string) => {
      return fetchWithAuth(`/api/projects/${id}/tree`)
    },
  },
  
  // Branches
  branches: {
    terminate: async (projectId: string, branchId: string, reason?: string) => {
      return fetchWithAuth(`/api/projects/${projectId}/branches/${branchId}/terminate`, {
        method: 'POST',
        body: JSON.stringify({ reason }),
      })
    },

    prune: async (projectId: string, branchId: string, cascade = false) => {
      const qs = cascade ? '?cascade=true' : '?cascade=false'
      return fetchWithAuth(`/api/projects/${projectId}/branches/${branchId}${qs}`, {
        method: 'DELETE',
      })
    },

    rename: async (projectId: string, branchId: string, name: string) => {
      return fetchWithAuth(`/api/projects/${projectId}/branches/${branchId}`, {
        method: 'PATCH',
        body: JSON.stringify({ name }),
      })
    },
  },

  // Drafts (Round model v2: per-question autosave)
  drafts: {
    upsert: async (
      projectId: string,
      branchId: string,
      questionId: string,
      payload: {
        draftText: string
        draftStatus: 'answered' | 'partial' | 'skipped'
        selectedOptions?: string[]
        otherText?: string
      },
    ) => {
      return fetchWithAuth(
        `/api/projects/${projectId}/branches/${branchId}/questions/${questionId}/draft`,
        { method: 'PUT', body: JSON.stringify(payload) },
      )
    },

    remove: async (projectId: string, branchId: string, questionId: string) => {
      return fetchWithAuth(
        `/api/projects/${projectId}/branches/${branchId}/questions/${questionId}/draft`,
        { method: 'DELETE' },
      )
    },

    listForRound: async (projectId: string) => {
      return fetchWithAuth<{
        drafts: Array<{
          _id: string
          projectId: string
          branchId: string
          questionId: string
          draftText: string
          draftStatus: 'answered' | 'partial' | 'skipped'
          selectedOptions?: string[]
          otherText?: string
          updatedAt: number
        }>
      }>(`/api/projects/${projectId}/round/drafts`)
    },
  },
  
  // Export
  export: {
    generate: async (
      projectId: string,
      format: 'latex' | 'markdown' | 'json' | 'mermaid' | 'graphviz',
      options?: { includeConversation?: boolean; includeTreeDiagram?: boolean }
    ) => {
      return fetchWithAuth<{ filename: string; content: string; mimeType: string }>(
        `/api/projects/${projectId}/export`,
        {
          method: 'POST',
          body: JSON.stringify({ format, ...(options ?? {}) }),
        }
      )
    },
  },
  
  // Commands
  command: async (
    projectId: string,
    command: 'continue' | 'retry' | 'reset' | 'help' | 'generate',
  ) => {
    return fetchWithAuth(`/api/projects/${projectId}/command`, {
      method: 'POST',
      body: JSON.stringify({ command }),
    })
  },
}

/**
 * Get SSE URL for streaming endpoints
 */
export function getSSEUrl(endpoint: string): string {
  const { token } = useAuthStore.getState()
  const url = new URL(`${API_URL}${endpoint}`)
  if (token) {
    url.searchParams.set('token', token)
  }
  return url.toString()
}

export default api
